import { useState, useRef } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Upload, Download, CheckCircle, AlertTriangle, Loader2, ChevronRight, FileText, Plus, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuth } from "@/lib/authContext";

interface TreatmentSummary { treatment: string; total: number; valid: number; errors: number }

interface BuildResponse {
  file: string;
  filename: string;
  validCount: number;
  errorCount: number;
  totalExtracted: number;
  summary: TreatmentSummary[];
}

interface Props { onClose: () => void }

type Step = "upload" | "extracting" | "result";

function FilePill({ file, onRemove }: { file: File; onRemove: () => void }) {
  return (
    <div className="flex items-center gap-2 rounded-lg border bg-muted/40 px-3 py-2.5">
      <FileText className="w-4 h-4 text-blue-500 shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-foreground truncate">{file.name}</p>
        <p className="text-[10px] text-muted-foreground">{(file.size / 1024).toFixed(0)} KB</p>
      </div>
      <button onClick={onRemove} className="text-muted-foreground hover:text-foreground shrink-0">
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

export function FixedAssetsCSModal({ onClose }: Props) {
  const { session } = useAuth();
  const inputRef = useRef<HTMLInputElement>(null);
  const [step, setStep] = useState<Step>("upload");
  const [files, setFiles] = useState<File[]>([]);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<BuildResponse | null>(null);

  function addFiles(incoming: FileList | null) {
    if (!incoming) return;
    const arr = Array.from(incoming).filter(f => f.type === "application/pdf" || f.name.endsWith(".pdf"));
    setFiles(prev => {
      const combined = [...prev, ...arr];
      return combined.slice(0, 2); // max 2
    });
    setError(null);
  }

  function removeFile(idx: number) {
    setFiles(prev => prev.filter((_, i) => i !== idx));
  }

  async function extract() {
    if (files.length === 0) return;
    setError(null);
    setStep("extracting");
    try {
      const fd = new FormData();
      files.forEach(f => fd.append("files", f));
      const res = await fetch("/api/tools/fixed-assets-cs/build", {
        method: "POST",
        headers: { Authorization: `Bearer ${session?.access_token ?? ""}` },
        body: fd,
      });
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j.message || `Request failed: ${res.status}`);
      }
      const data: BuildResponse = await res.json();
      setResult(data);
      setStep("result");
    } catch (e: any) {
      setError(e?.message ?? "Extraction failed. Please try again.");
      setStep("upload");
    }
  }

  function download() {
    if (!result) return;
    const link = document.createElement("a");
    link.href = `data:application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;base64,${result.file}`;
    link.download = result.filename;
    link.click();
  }

  function reset() {
    setFiles([]);
    setResult(null);
    setError(null);
    setStep("upload");
  }

  const canAddMore = files.length < 2;

  return (
    <Dialog open onOpenChange={v => { if (!v) onClose(); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle>Fixed Assets CS Import Builder</DialogTitle>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          {(["upload", "extracting", "result"] as Step[]).map((s, i) => (
            <span key={s} className="flex items-center gap-1">
              <span className={cn(
                "px-2 py-0.5 rounded-full font-medium",
                step === s ? "bg-blue-100 text-blue-700" : "text-muted-foreground"
              )}>
                {i + 1}. {s === "upload" ? "Upload PDF(s)" : s === "extracting" ? "Extracting" : "Results"}
              </span>
              {i < 2 && <ChevronRight className="w-3 h-3 shrink-0" />}
            </span>
          ))}
        </div>

        {/* ── Step 1: Upload ── */}
        {step === "upload" && (
          <div className="space-y-3 pt-1">
            <p className="text-sm text-muted-foreground">
              Upload 1 or 2 Fixed Assets CS report PDFs (e.g. Tax + Book). Gemini AI extracts
              all asset rows and builds a ready-to-import workbook.
            </p>

            {/* Existing files */}
            {files.length > 0 && (
              <div className="space-y-2">
                {files.map((f, i) => (
                  <FilePill key={i} file={f} onRemove={() => removeFile(i)} />
                ))}
              </div>
            )}

            {/* Drop zone — only show if < 2 files */}
            {canAddMore && (
              <div
                className={cn(
                  "border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors",
                  dragging ? "border-blue-400 bg-blue-50" : "border-slate-200 hover:border-slate-300 bg-slate-50",
                )}
                onClick={() => inputRef.current?.click()}
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => { e.preventDefault(); setDragging(false); addFiles(e.dataTransfer.files); }}
              >
                <input
                  ref={inputRef}
                  type="file"
                  className="hidden"
                  accept=".pdf"
                  multiple
                  onChange={e => addFiles(e.target.files)}
                />
                {files.length === 0 ? (
                  <>
                    <Upload className="w-7 h-7 mx-auto mb-2 text-slate-400" />
                    <p className="text-sm font-medium text-foreground">Click or drag PDF(s) here</p>
                    <p className="text-xs text-muted-foreground mt-1">Tax Asset Detail, Book Asset Detail, etc.</p>
                  </>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                    <Plus className="w-4 h-4" />
                    Add second PDF (Book or Tax treatment)
                  </div>
                )}
              </div>
            )}

            {files.length === 2 && (
              <p className="text-xs text-blue-600 bg-blue-50 border border-blue-200 rounded px-3 py-2">
                Both treatments will be extracted and output as separate import sheets in one workbook.
              </p>
            )}

            {error && (
              <p className="text-xs text-destructive flex items-start gap-1.5">
                <AlertTriangle className="w-3.5 h-3.5 shrink-0 mt-0.5" />
                {error}
              </p>
            )}

            <Button className="w-full" disabled={files.length === 0} onClick={extract}>
              Extract Assets →
            </Button>
          </div>
        )}

        {/* ── Step 2: Extracting ── */}
        {step === "extracting" && (
          <div className="py-10 flex flex-col items-center gap-4 text-center">
            <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
            <div>
              <p className="text-sm font-medium text-foreground">
                Analyzing {files.length > 1 ? `${files.length} PDFs` : "PDF"} with Gemini AI…
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Reading asset rows, dates, costs, and depreciation fields
              </p>
            </div>
            <p className="text-xs text-muted-foreground">
              {files.length > 1 ? "Processing each treatment sequentially — " : ""}Usually 15–45 seconds
            </p>
          </div>
        )}

        {/* ── Step 3: Results ── */}
        {step === "result" && result && (
          <div className="space-y-4 pt-1">
            <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
              <div className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600 shrink-0" />
                <span className="text-sm font-semibold">Workbook Ready</span>
              </div>

              {/* Per-treatment breakdown if multiple */}
              {result.summary?.length > 1 ? (
                <div className="space-y-2">
                  {result.summary.map(s => (
                    <div key={s.treatment} className="rounded-md border bg-background p-2.5">
                      <p className="text-[11px] font-semibold text-muted-foreground uppercase tracking-wide mb-1.5">
                        {s.treatment} Treatment
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <div className="text-center">
                          <p className="text-lg font-bold text-foreground">{s.total}</p>
                          <p className="text-[10px] text-muted-foreground">found</p>
                        </div>
                        <div className="text-center">
                          <p className="text-lg font-bold text-green-700">{s.valid}</p>
                          <p className="text-[10px] text-green-600">ready</p>
                        </div>
                        <div className="text-center">
                          <p className={cn("text-lg font-bold", s.errors > 0 ? "text-amber-700" : "text-slate-400")}>
                            {s.errors}
                          </p>
                          <p className={cn("text-[10px]", s.errors > 0 ? "text-amber-600" : "text-slate-400")}>
                            review
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="grid grid-cols-3 gap-2">
                  <div className="rounded-md bg-slate-50 border p-2.5 text-center">
                    <p className="text-xl font-bold">{result.totalExtracted}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">rows found</p>
                  </div>
                  <div className="rounded-md bg-green-50 border border-green-200 p-2.5 text-center">
                    <p className="text-xl font-bold text-green-700">{result.validCount}</p>
                    <p className="text-[10px] text-green-600 mt-0.5">import-ready</p>
                  </div>
                  <div className={cn("rounded-md border p-2.5 text-center", result.errorCount > 0 ? "bg-amber-50 border-amber-200" : "bg-slate-50")}>
                    <p className={cn("text-xl font-bold", result.errorCount > 0 ? "text-amber-700" : "text-slate-400")}>
                      {result.errorCount}
                    </p>
                    <p className={cn("text-[10px] mt-0.5", result.errorCount > 0 ? "text-amber-600" : "text-slate-400")}>
                      need review
                    </p>
                  </div>
                </div>
              )}

              {result.errorCount > 0 && (
                <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                  Rows with errors are excluded from the import sheet(s) and listed in
                  <strong> Validation_Errors</strong> for review.
                </p>
              )}
            </div>

            <div className="rounded-lg border p-3 space-y-1.5 text-xs text-muted-foreground">
              <p className="font-semibold text-foreground text-[11px] uppercase tracking-wide mb-2">Workbook Sheets</p>
              {result.summary?.length > 1 ? (
                <>
                  {result.summary.map(s => (
                    <div key={s.treatment} className="flex gap-2 items-start">
                      <span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded shrink-0">FA_CS_Import_{s.treatment}</span>
                      <span>Import-ready {s.treatment} treatment rows</span>
                    </div>
                  ))}
                  {result.summary.map(s => (
                    <div key={`stg-${s.treatment}`} className="flex gap-2 items-start">
                      <span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded shrink-0">Staging_{s.treatment}</span>
                      <span>All normalized {s.treatment} rows</span>
                    </div>
                  ))}
                </>
              ) : (
                <>
                  <div className="flex gap-2"><span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded shrink-0">FA_CS_Import</span><span>Clean rows ready for Fixed Assets CS</span></div>
                  <div className="flex gap-2"><span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded shrink-0">Staging_Normalized</span><span>All rows with normalized values</span></div>
                </>
              )}
              <div className="flex gap-2"><span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded shrink-0">Validation_Errors</span><span>Excluded rows with error details</span></div>
              <div className="flex gap-2"><span className="font-mono text-[10px] bg-muted px-1.5 py-0.5 rounded shrink-0">README</span><span>Step-by-step import instructions</span></div>
            </div>

            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={reset}>← New Files</Button>
              <Button className="flex-1 gap-2" onClick={download}>
                <Download className="w-4 h-4" />
                Download {result.filename}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
